/**
 * Run-stream event validation (prompt.md §48, §49, §77).
 *
 * WHY this is separate from the transports: events arrive over three very
 * different channels (WebSocket frames, polling responses, and the initial
 * replay), and all three must be validated the same way before the UI reducer
 * sees them. A malformed frame is dropped here rather than crashing a
 * transcript mid-run.
 */

import {
  type RunEventPayloadMap,
  type RunEventType,
  type RunStreamEvent,
  RUN_EVENT_SCHEMA_VERSION,
  RUN_EVENT_TYPES,
} from "@dayvise/event-protocol";
import { type EventId, type RunId, unsafeId } from "@dayvise/domain";

const KNOWN_EVENT_TYPES = new Set<string>(RUN_EVENT_TYPES);

/** Events after which the client should stop expecting more on this stream. */
export const TERMINAL_RUN_EVENT_TYPES = ["run.completed"] as const satisfies readonly RunEventType[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Returns `null` for anything the reducer must not be asked to handle. */
export function normalizeRunEvent(raw: unknown): RunStreamEvent | null {
  if (!isRecord(raw)) return null;
  const eventId = raw.eventId;
  const runId = raw.runId;
  const sequence = raw.sequence;
  const type = raw.type;
  const timestamp = raw.timestamp;
  const payload = raw.payload;
  if (typeof eventId !== "string" || eventId.length === 0) return null;
  if (typeof runId !== "string" || runId.length === 0) return null;
  if (typeof sequence !== "number" || !Number.isFinite(sequence)) return null;
  if (typeof type !== "string" || !KNOWN_EVENT_TYPES.has(type)) return null;
  if (typeof timestamp !== "string") return null;
  if (!isRecord(payload)) return null;
  const schemaVersion = typeof raw.schemaVersion === "number" ? raw.schemaVersion : RUN_EVENT_SCHEMA_VERSION;
  return {
    eventId: unsafeId<"event">(eventId) as EventId,
    runId: unsafeId<"run">(runId) as RunId,
    sequence,
    timestamp,
    type: type as RunEventType,
    schemaVersion,
    payload: payload as RunEventPayloadMap[RunEventType],
  };
}

/** True once the run has stopped producing events for good. */
export function isTerminalRunEvent(event: RunStreamEvent): boolean {
  if ((TERMINAL_RUN_EVENT_TYPES as readonly string[]).includes(event.type)) return true;
  if (event.type !== "run.error") return false;
  const retryable = (event.payload as { retryable?: unknown }).retryable;
  return retryable === false;
}
